import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Check } from 'lucide-react';
import { PhoneInput } from '@/components/ui/phone-input';
import { api } from '@/lib/api';

const contactSchema = z.object({
  name: z.string().min(2, 'Nom trop court'),
  email: z.string().email('Email invalide'),
  phone: z.string().optional(),
  role: z.string().optional(),
  country: z.string().optional(),
  subject: z.string().min(1, 'Choisissez un sujet'),
  message: z.string().min(10, 'Message trop court (10 caractères min)'),
});

type ContactFormData = z.infer<typeof contactSchema>;

const SUBJECTS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'demo', label: 'Demande de démo' },
  { value: 'presale', label: 'Question avant achat' },
  { value: 'support', label: 'Support technique' },
  { value: 'partnership', label: 'Partenariat · revente' },
  { value: 'press', label: 'Presse · média' },
  { value: 'other', label: 'Autre' },
];

const COUNTRIES: ReadonlyArray<string> = [
  'Cameroun',
  'Sénégal',
  "Côte d'Ivoire",
  'Mali',
  'Burkina Faso',
  'Bénin',
  'Togo',
  "Autre pays d'Afrique",
  'Autre',
];

const ROLES: ReadonlyArray<string> = [
  'Gérant · propriétaire',
  'Caissier',
  'Comptable',
  'Responsable IT',
  'Autre',
];

const INPUT_CLASSES =
  'w-full border-2 border-charcoal-800 bg-white px-[14px] py-3 font-marketing-body text-[15px] text-charcoal-800 placeholder:text-charcoal-400 outline-none transition-shadow duration-150 focus:shadow-[3px_3px_0_var(--color-lime-500)]';

const LABEL_CLASSES =
  'mb-2 block font-marketing-mono text-[11px] font-medium uppercase tracking-[0.08em] text-charcoal-600';

type Props = {
  /** Valeur du sujet pré-sélectionnée à l'ouverture du formulaire (ex. 'demo'). */
  defaultSubject?: string;
};

export default function ContactFormV2({ defaultSubject }: Props) {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: defaultSubject ? { subject: defaultSubject } : undefined,
  });

  const onSubmit = async (data: ContactFormData) => {
    setError(null);
    try {
      await api.post('/contact', {
        name: data.name,
        email: data.email,
        phone: data.phone,
        subject: data.subject,
        message: data.message,
        role: data.role,
        country: data.country,
      });
      setSubmitted(true);
    } catch {
      setError("Impossible d'envoyer le message. Réessaye ou écris à contact@velko-pos.com.");
    }
  };

  if (submitted) {
    return (
      <div className="border-[3px] border-charcoal-800 bg-lime-500 p-6 shadow-neobrutal-6 md:p-9 md:shadow-neobrutal-10">
        <div className="mb-5 inline-flex h-12 w-12 items-center justify-center border-2 border-charcoal-800 bg-white shadow-neobrutal-3">
          <Check className="h-6 w-6 text-charcoal-800" strokeWidth={2.8} />
        </div>
        <h3 className="font-marketing-display text-2xl font-bold tracking-[-0.02em] text-charcoal-800">
          Message reçu.
        </h3>
        <p className="mt-2 font-marketing-body text-[15px] text-charcoal-800">
          On te répond en moins de 24h ouvrables. Pense à vérifier tes spams.
        </p>
        <button
          type="button"
          onClick={() => {
            reset();
            setSubmitted(false);
          }}
          className="mt-6 inline-flex items-center gap-2 border-2 border-charcoal-800 bg-white px-5 py-2.5 font-marketing-display text-[13px] font-bold text-charcoal-800 shadow-neobrutal-3 transition-transform duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-5"
        >
          Envoyer un autre message
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="border-[3px] border-charcoal-800 bg-paper p-6 shadow-neobrutal-6 md:p-9 md:shadow-neobrutal-10"
      noValidate
    >
      <div className="grid gap-4 md:grid-cols-2 md:gap-[18px]">
        <div>
          <label htmlFor="c-name" className={LABEL_CLASSES}>
            Nom complet<span className="ml-1 text-terracotta-500">*</span>
          </label>
          <input
            id="c-name"
            type="text"
            placeholder="Awa Ndiaye"
            autoComplete="name"
            className={INPUT_CLASSES}
            {...register('name')}
          />
          {errors.name && (
            <p className="mt-1.5 font-marketing-mono text-[11px] text-terracotta-600">
              {errors.name.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="c-role" className={LABEL_CLASSES}>
            Rôle
          </label>
          <select id="c-role" className={INPUT_CLASSES} {...register('role')}>
            {ROLES.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="c-email" className={LABEL_CLASSES}>
            Email<span className="ml-1 text-terracotta-500">*</span>
          </label>
          <input
            id="c-email"
            type="email"
            placeholder="awa@boutique.cm"
            autoComplete="email"
            className={INPUT_CLASSES}
            {...register('email')}
          />
          {errors.email && (
            <p className="mt-1.5 font-marketing-mono text-[11px] text-terracotta-600">
              {errors.email.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="c-phone" className={LABEL_CLASSES}>
            Téléphone · WhatsApp
          </label>
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <div className="[&_input]:border-2 [&_input]:border-charcoal-800 [&_input]:bg-white [&_input]:px-[14px] [&_input]:py-3 [&_input]:font-marketing-body [&_input]:text-[15px] [&_input]:text-charcoal-800 [&_input]:outline-none [&_input]:transition-shadow [&_input]:duration-150 focus-within:[&_input]:shadow-[3px_3px_0_var(--color-lime-500)]">
                <PhoneInput value={field.value ?? ''} onChange={field.onChange} />
              </div>
            )}
          />
        </div>

        <div>
          <label htmlFor="c-country" className={LABEL_CLASSES}>
            Pays
          </label>
          <select id="c-country" className={INPUT_CLASSES} {...register('country')}>
            {COUNTRIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="c-subject" className={LABEL_CLASSES}>
            Sujet<span className="ml-1 text-terracotta-500">*</span>
          </label>
          <select id="c-subject" className={INPUT_CLASSES} {...register('subject')}>
            <option value="">—</option>
            {SUBJECTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          {errors.subject && (
            <p className="mt-1.5 font-marketing-mono text-[11px] text-terracotta-600">
              {errors.subject.message}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="c-message" className={LABEL_CLASSES}>
          Ton message<span className="ml-1 text-terracotta-500">*</span>
        </label>
        <textarea
          id="c-message"
          rows={6}
          placeholder="Décris ton commerce, tes attentes, tes contraintes. On lit tout avant de répondre."
          className={`${INPUT_CLASSES} min-h-[140px] resize-y`}
          {...register('message')}
        />
        {errors.message && (
          <p className="mt-1.5 font-marketing-mono text-[11px] text-terracotta-600">
            {errors.message.message}
          </p>
        )}
      </div>

      {error && (
        <p className="mt-4 border-2 border-terracotta-500 bg-white px-4 py-3 font-marketing-body text-[13px] text-terracotta-600">
          {error}
        </p>
      )}

      <div className="mt-6 flex flex-col items-start justify-between gap-4 border-t-2 border-dashed border-charcoal-200 pt-5 sm:flex-row sm:items-center">
        <span className="font-marketing-mono text-[11px] uppercase tracking-[0.08em] text-charcoal-500">
          Réponse ≤ 24h · aucune newsletter automatique
        </span>
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 border-2 border-charcoal-800 bg-lime-500 px-7 py-3.5 font-marketing-display text-[15px] font-bold text-charcoal-800 shadow-neobrutal-4 transition-transform duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-6 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? 'Envoi…' : 'Envoyer'}
          <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
        </button>
      </div>
    </form>
  );
}
